import { useCallback, useEffect, useRef } from 'react';
import { COOKIE_KEY, cookieUtils } from '@app/app/cookie';
import { addBreadcrumb } from '@sentry/react';
import * as Sentry from '@sentry/react';
import { usePersistedAccountsStore } from '@app/app/persist';

import { analytics } from './analytics';
import { PlatformName } from './types';

export const handleRegisterByGoogle = (sendMarketingEmails: 'allow' | 'deny') => {
  cookieUtils.set(COOKIE_KEY.sendMarketingEmail, sendMarketingEmails, {
    path: '/',
    expires: undefined,
  });
};

export const trackOAuthAnalytics = (accountsOnDevice: number, userId: string) => {
  if (!userId || userId === 'undefined' || userId === 'null') {
    console.error('trackOAuthAnalytics: Invalid userId', { userId, accountsOnDevice });
    Sentry.captureException(new Error('trackOAuthAnalytics: Invalid userId'));
    return;
  }

  const isOAuthReg = cookieUtils.get(COOKIE_KEY.oAuthReg);
  const isOAuthLog = cookieUtils.get(COOKIE_KEY.oAuthLog);

  if (!isOAuthReg && !isOAuthLog) {
    // Нет OAuth куки - это нормальная ситуация, не логируем в Sentry
    return;
  }

  try {
    if (isOAuthReg) {
      if (typeof analytics?.emitEvent !== 'function') {
        console.error('Analytics GTM not ready for sign_up event');
        Sentry.captureException(new Error('Analytics GTM not ready for sign_up event'));
        return;
      }

      cookieUtils.remove(COOKIE_KEY.oAuthReg);
      cookieUtils.remove(COOKIE_KEY.sendMarketingEmail);
    }

    if (isOAuthLog) {
      if (typeof analytics?.emitEvent !== 'function') {
        console.error('Analytics GTM not ready for login event');
        Sentry.captureException(new Error('Analytics GTM not ready for login event'));
        return;
      }

      cookieUtils.remove(COOKIE_KEY.oAuthLog);
    }
  } catch (e) {
    console.error('trackOAuthAnalytics error:', e);
    if (e instanceof Error) {
      Sentry.addBreadcrumb({
        message: e.message,
        data: {
          error: e,
          userId,
          accountsOnDevice,
          isOAuthReg: !!isOAuthReg,
          isOAuthLog: !!isOAuthLog,
        },
      });
      Sentry.captureException(e);
    }
  }
};

export const trackOAuthAnalyticsWithWait = (accountsOnDevice: number, userId: string) => {
  if (!userId || userId === 'undefined' || userId === 'null' || userId.length <= 0) {
    console.error('trackOAuthAnalyticsWithWait: Invalid userId', { userId, accountsOnDevice });
    Sentry.captureException(new Error('trackOAuthAnalyticsWithWait: Invalid userId'));
    return;
  }

  const isOAuthReg = cookieUtils.get(COOKIE_KEY.oAuthReg);
  const isOAuthLog = cookieUtils.get(COOKIE_KEY.oAuthLog);
  const sendMarketingEmails = cookieUtils.get(COOKIE_KEY.sendMarketingEmail);

  if (!isOAuthReg && !isOAuthLog) {
    // Нет OAuth куки - это нормальная ситуация, не логируем в Sentry
    return;
  }

  try {
    if (isOAuthReg) {
      analytics.emitEvent(
        'sign_up',
        {
          [PlatformName.GTM]: {
            accounts_on_device: accountsOnDevice,
            method: 'google',
            send_marketing_email: sendMarketingEmails,
            user_id: Number(userId),
          },
        },
        () => {
          // Удаляем куки только после успешной отправки события
          cookieUtils.remove(COOKIE_KEY.oAuthReg);
          cookieUtils.remove(COOKIE_KEY.sendMarketingEmail);

          addBreadcrumb({
            message: 'OAuth sign_up event sent',
            category: 'oauth.analytics',
            level: 'info',
            data: { userId, eventName: 'sign_up' },
          });
        },
      );
    }

    if (isOAuthLog) {
      analytics.emitEvent(
        'login',
        {
          [PlatformName.GTM]: {
            method: 'google',
          },
        },
        () => {
          // Удаляем куки только после успешной отправки события
          cookieUtils.remove(COOKIE_KEY.oAuthLog);

          addBreadcrumb({
            message: 'OAuth login event sent',
            category: 'oauth.analytics',
            level: 'info',
            data: { userId, eventName: 'login' },
          });
        },
      );
    }
  } catch (e) {
    console.error('trackOAuthAnalyticsWithWait error:', e);
    if (e instanceof Error) {
      addBreadcrumb({
        message: e.message,
        data: {
          error: e,
          userId,
          accountsOnDevice,
          isOAuthReg: !!isOAuthReg,
          isOAuthLog: !!isOAuthLog,
        },
      });
      Sentry.captureException(e);
    }
  }
};

/**
 * Кастомный хук для OAuth аналитики
 * Использует один раз при появлении userId
 */
export const useOAuthAnalytics = (userId: string | number | undefined) => {
  const { accounts } = usePersistedAccountsStore();
  const hasTrackedRef = useRef<Set<string>>(new Set());

  const trackOAuth = useCallback(() => {
    const userIdStr = String(userId);

    // Проверяем наличие OAuth куки
    const keys = [COOKIE_KEY.oAuthLog, COOKIE_KEY.oAuthReg];
    const shouldTrack = keys.some((key) => cookieUtils.get(key));

    // Создаем уникальный ключ для отслеживания
    const trackingKey = `${userIdStr}_${keys.filter((key) => cookieUtils.get(key)).join('_')}`;

    if (shouldTrack && !hasTrackedRef.current.has(trackingKey)) {
      console.debug('🎯 OAuth Analytics triggered from useOAuthAnalytics', {
        userId: userIdStr,
        accountsSize: accounts.size,
        hasOAuthReg: !!cookieUtils.get(COOKIE_KEY.oAuthReg),
        hasOAuthLog: !!cookieUtils.get(COOKIE_KEY.oAuthLog),
        trackingKey,
      });

      // Отправляем аналитику
      trackOAuthAnalyticsWithWait(accounts.size, userIdStr);

      // Отмечаем как отправленное
      hasTrackedRef.current.add(trackingKey);
    }
  }, [userId, accounts.size]);

  useEffect(() => {
    trackOAuth();
  }, [trackOAuth]);

  return { trackOAuth };
};
